import { Input } from '@velocity/ui'

import { useContent } from '@ngb-frontend/shared/context'
import { validation as v } from '@ngb-frontend/shared/utils'

import { FormField } from '../FormField/FormField'

import type { FormFieldWrapper } from '@ngb-frontend/shared/types'
import type { ChangeEvent, FC } from 'react'

type BasicTextFieldProps = FormFieldWrapper & {
  /**
   * Display token — drives the input id suffix, aria-label and e2e hook
   * (e.g. `field="city"` → id `city-input`, aria-label `city`).
   */
  field: string
  /** Content key resolved for the label, e.g. 'fields.city.label'. */
  labelKey: string
  /** Extra props merged into the Velocity Input's `inputProps` (e.g. `{ maxLength: 10 }`). */
  inputProps?: Record<string, unknown>
  /**
   * Optional change-handler factory. Receives the final-form `input` so the wrapper
   * can guard/transform and still delegate (e.g. PostcodeField's length cap).
   * Defaults to the standard final-form `input.onChange`.
   */
  onChange?: (input: {
    onChange: (...args: unknown[]) => void
  }) => (event: ChangeEvent<HTMLInputElement>) => void
}

/**
 * Shared implementation for the near-identical single-line text fields
 * (CityField, CountyField, StreetNameField, HouseNumberField, PostcodeField).
 * Each of those is a thin wrapper supplying `field` + `labelKey` (+ an escape hatch
 * where needed); the FormField + Input + required-validation wiring lives here once.
 * NGRE2E-4037.
 *
 * Internal building block — not re-exported from the library barrel.
 */
export const BasicTextField: FC<BasicTextFieldProps> = ({
  field,
  labelKey,
  inputProps,
  onChange,
  name,
  classes,
  noMargin,
  label,
  hideLabel,
  disabled,
  required = true,
}) => {
  const c = useContent()
  return (
    <FormField<string>
      classes={classes}
      name={name ?? field}
      id={`${field}-input`}
      validator={
        required
          ? v.apply(v.required, {
              message: c('errors.required'),
              type: 'errorType',
            })
          : undefined
      }
      label={label || c(labelKey)}
      hideLabel={hideLabel}
      required={required}
      noMargin={noMargin}
      disabled={disabled}
      renderKey={required ? '1' : '0'}
    >
      {({ input }) => (
        <Input
          inputProps={{
            'aria-label': field,
            'data-e2e-component': `${field}-input`,
            ...inputProps,
          }}
          {...input}
          onChange={onChange ? onChange(input) : input.onChange}
          type="string"
          required={false}
          disabled={disabled}
        />
      )}
    </FormField>
  )
}
